import { useState } from "react";
import { Modal, message } from "antd";
import { ApolloError } from "@apollo/client";
import { CircleCheck, Copy, ExternalLink } from "lucide-react";

import OllieAvatar from "components/ollie-avatar/ollie-avatar";
import { useInsertPlanReviewAhjSubmissionAcknowledgmentMutation } from "graphql/mutations/insert-plan-review-ahj-submission-acknowledgment.generated";
import { isAuthorityRole, PlanReviewReviewOutcome } from "constants/field-values";
import { copyOllieIdToClipboard } from "utils/ollie-id";

interface AhjSubmissionAcknowledgmentCheck {
	reviewOutcome?: string | null;
	/** Confirmations of the logged user for this submission. The select permission only returns the user's own rows. */
	userAcknowledgments?: { id: string }[] | null;
	userRole?: string | null;
}

/**
 * Returns true when the report of a submission must be blocked by the acknowledgment modal:
 * the submission is APPROVED, the logged user has not confirmed the modal for this submission
 * yet, and the user is not an authority user (the AHJ reviewer does not submit an application
 * to itself). Every user confirms separately.
 */
export const isAhjSubmissionAcknowledgmentRequired = ({
	reviewOutcome,
	userAcknowledgments,
	userRole
}: AhjSubmissionAcknowledgmentCheck): boolean =>
	reviewOutcome === PlanReviewReviewOutcome.APPROVED &&
	(userAcknowledgments?.length ?? 0) === 0 &&
	!isAuthorityRole(userRole);

// The unique key on (job_id, user_id) rejects a second confirmation. That means the confirmation
// is already saved, for example from another browser tab, so it is not an error for the user.
const isAlreadyAcknowledgedError = (error: unknown): boolean =>
	error instanceof ApolloError &&
	error.graphQLErrors.some(graphQLError => graphQLError.extensions?.code === "constraint-violation");

interface PlanReviewApprovalAcknowledgmentModalProps {
	jobId: string;
	ollieId?: string | null;
	propertyName?: string | null;
	jurisdictionName?: string | null;
	jurisdictionWebsiteUrl?: string | null;
}

/**
 * Blocking modal for an APPROVED submission. It explains that an Ollie approval is not an AHJ
 * approval, gives the next steps to submit the application to the AHJ, and stays open until the
 * user confirms it.
 *
 * The confirmation is saved per user and submission in plan_review_ahj_submission_acknowledgments,
 * so the modal does not open again for this user on any screen or device. Other users of the
 * same organization still have to confirm it. The modal cannot be closed in any other way.
 */
const PlanReviewApprovalAcknowledgmentModal = ({
	jobId,
	ollieId,
	propertyName,
	jurisdictionName,
	jurisdictionWebsiteUrl
}: PlanReviewApprovalAcknowledgmentModalProps) => {
	// The parent closes the modal when the job subscription returns the saved row. This flag
	// closes it at once after the mutation, so the user does not wait for the next subscription update.
	const [isAcknowledged, setIsAcknowledged] = useState(false);

	const [insertAhjSubmissionAcknowledgment, { loading: isSaving }] =
		useInsertPlanReviewAhjSubmissionAcknowledgmentMutation();

	const projectName = propertyName || "your project";
	const ahjName = jurisdictionName || "the AHJ";

	const handleCopyOllieId = () => {
		if (ollieId) copyOllieIdToClipboard(ollieId);
	};

	const handleAcknowledge = async () => {
		try {
			const result = await insertAhjSubmissionAcknowledgment({ variables: { jobId } });

			// A null row means that the role is not allowed to confirm this submission.
			if (!result.data?.insert_plan_review_ahj_submission_acknowledgments_one) {
				throw new Error(`plan_review_ahj_submission_acknowledgments row for job ${jobId} was not inserted`);
			}

			setIsAcknowledged(true);
		} catch (error) {
			if (isAlreadyAcknowledgedError(error)) {
				setIsAcknowledged(true);
				return;
			}

			console.error(
				`[PlanReview] handleAcknowledge: could not save the AHJ submission acknowledgment for job ${jobId}`,
				error
			);
			message.error("We couldn't save your confirmation. Please try again.");
		}
	};

	const ollieIdChip = ollieId ? <span className="praa-ollie-id-chip">{ollieId}</span> : null;

	const goToAhjPortalLink = jurisdictionWebsiteUrl ? (
		<a className="praa-portal-link" href={jurisdictionWebsiteUrl} target="_blank" rel="noopener noreferrer">
			Go to AHJ Submittal Portal
			<ExternalLink size={14} className="praa-portal-link-icon" />
		</a>
	) : null;

	return (
		<Modal
			open={!isAcknowledged}
			footer={null}
			title={null}
			closable={false}
			maskClosable={false}
			keyboard={false}
			centered
			width={576}
			className="praa-modal"
			rootClassName="praa-modal-root"
		>
			<div className="praa-hero">
				<div className="praa-hero-owl">
					<OllieAvatar mood="pass" className="praa-hero-owl-image" />
				</div>
				<span className="praa-approved-badge">
					<CircleCheck size={14} className="praa-approved-badge-icon" />
					Ollie Pre-Screening Approved
				</span>
				<h2 className="praa-title">Your Submittal Package is Ready!</h2>
				<p className="praa-subtitle">
					Ollie has approved the pre-screening package for <strong>{projectName}</strong>.
				</p>
			</div>

			<div className="praa-body">
				{ollieId && (
					<div className="praa-ollie-id-card">
						<div className="praa-ollie-id-info">
							<span className="praa-ollie-id-label">Your Ollie Pre-Screening ID</span>
							<span className="praa-ollie-id-value">{ollieId}</span>
						</div>
						<button type="button" className="praa-copy-btn" onClick={handleCopyOllieId}>
							<Copy size={16} className="praa-copy-btn-icon" />
							Copy Ollie ID
						</button>
					</div>
				)}

				<div className="praa-next-steps">
					<h3 className="praa-next-steps-title">What to Do Next</h3>

					<div className="praa-step">
						<span className="praa-step-number">1</span>
						<div className="praa-step-content">
							<p className="praa-step-title">Submit your formal application to the AHJ</p>
							<p className="praa-step-text">
								Upload your plans and complete your official submittal directly with{" "}
								<span className="praa-step-highlight">{ahjName}</span>.
							</p>
							{goToAhjPortalLink}
						</div>
					</div>

					{ollieId && (
						<div className="praa-step">
							<span className="praa-step-number">2</span>
							<div className="praa-step-content">
								<p className="praa-step-title">Add your Ollie ID to your project name</p>
								<div className="praa-step-inline">
									<span className="praa-step-text">When filling out your AHJ application, include</span>
									{ollieIdChip}
								</div>
								<p className="praa-step-text praa-step-text-continued">
									in the project name so the reviewer can reference your pre-screened package.
								</p>
								<div className="praa-example">
									Example:{" "}
									<span className="praa-example-value">
										{projectName} [{ollieId}]
									</span>
								</div>
							</div>
						</div>
					)}
				</div>

				<div className="praa-notice">
					<strong>Please confirm before viewing your report:</strong> An Ollie approval does not represent an official
					AHJ approval or permit. You must still submit your application and complete the AHJ&apos;s official review
					process.
				</div>

				<button type="button" className="praa-confirm-btn" onClick={handleAcknowledge} disabled={isSaving}>
					<CircleCheck size={16} className="praa-confirm-btn-icon" />I Understand — View Submittal Report
				</button>
			</div>
		</Modal>
	);
};

export default PlanReviewApprovalAcknowledgmentModal;
